/**
 * Local-first chat orchestrator (mounted once, app-wide, in DashboardLayout).
 *
 * On login it:
 *   1. Hydrates the messenger store from IndexedDB → instant, offline-capable
 *      load (previews + unread counts render immediately from the full cache).
 *   2. Starts write-through persistence: any store change (live channel events,
 *      history loads, edits/deletes, cursor updates) is debounced-flushed back
 *      to IndexedDB.
 *   3. Runs a one-shot delta sync per conversation — "what changed since my last
 *      visit" (new / edited / deleted messages + delivered/read cursors) —
 *      merging the delta on top of the cache and advancing the watermark.
 *
 * All of it is best-effort: if IndexedDB or the sync channel is unavailable the
 * app still works via the network path.
 */

import { useEffect, useRef, useState } from 'react'
import { useAuthStore } from '@/features/auth/store/auth.store'
import type { Message as WireMessage } from '../services/messenger.wire'
import { syncConversation } from '../services/messenger.socket'
import { useMessengerStore } from '../store/messenger.store'
import {
  cacheConversations,
  cacheMessages,
  clearChatCache,
  getMeta,
  readAllConversations,
  readAllMessages,
  requestPersistentStorage,
  setMeta,
} from '../services/messenger.db'
import { trace } from '../services/trace'

const WATERMARK_KEY = 'syncWatermark'
const USER_KEY = 'userId'
/** Delta-sync retry budget — 1s, 2s, 4s, 8s, 16s. Covers a slow cold-start
 *  socket build without hammering a genuinely offline server. */
const SYNC_MAX_ATTEMPTS = 5
const SYNC_RETRY_BASE_MS = 1000

/** Subscribe to the store and debounce-persist dirty conversations + list. */
function startPersistence(): () => void {
  let lastMsgs = useMessengerStore.getState().wireMessagesByConv
  let lastConvs = useMessengerStore.getState().wireConversations
  const dirty = new Set<string>()
  let convsDirty = false
  let timer: ReturnType<typeof setTimeout> | null = null

  const flush = () => {
    timer = null
    const s = useMessengerStore.getState()
    if (convsDirty) {
      convsDirty = false
      const convs = [...s.wireConversations.values()]
      trace('cache', `write → IndexedDB: ${convs.length} conversations`)
      void cacheConversations(convs)
    }
    if (dirty.size > 0) {
      const ids = [...dirty]
      dirty.clear()
      const msgs: WireMessage[] = []
      for (const id of ids) {
        const arr = s.wireMessagesByConv.get(id)
        // Not a message still being sent. A file's optimistic row lives for as
        // long as its upload, pointing at blob: URLs of the picked file; cached,
        // a reload brought it back as a message that never went, showing
        // nothing. It is written once reconciled, with the stored URLs.
        if (arr) for (const m of arr) if (!s.pendingSends.has(m.id)) msgs.push(m)
      }
      if (msgs.length > 0) {
        trace('cache', `write → IndexedDB: ${msgs.length} messages across ${ids.length} conversation(s)`)
        void cacheMessages(msgs)
      }
    }
  }

  return useMessengerStore.subscribe((state) => {
    if (state.wireMessagesByConv !== lastMsgs) {
      for (const [id, arr] of state.wireMessagesByConv) {
        if (lastMsgs.get(id) !== arr) dirty.add(id)
      }
      lastMsgs = state.wireMessagesByConv
    }
    if (state.wireConversations !== lastConvs) {
      convsDirty = true
      lastConvs = state.wireConversations
    }
    if (dirty.size === 0 && !convsDirty) return
    if (timer) clearTimeout(timer)
    timer = setTimeout(flush, 800)
  })
}

export function useLocalChatSync(): void {
  const me = useAuthStore((s) => s.user)
  const bootRef = useRef(false)
  const syncedRef = useRef(false)

  // 1 + 2 — hydrate from cache, then start write-through. Runs once per login.
  useEffect(() => {
    if (!me?.id || bootRef.current) return
    bootRef.current = true
    let disposed = false
    let unsub: (() => void) | null = null

    ;(async () => {
      void requestPersistentStorage()

      // User guard — a different user on this browser gets a clean cache.
      const cachedUser = await getMeta(USER_KEY)
      if (cachedUser && cachedUser !== me.id) {
        trace('cache', `different user (${cachedUser?.slice(0, 8)} → ${me.id.slice(0, 8)}) → clearChatCache()`)
        await clearChatCache()
      }
      await setMeta(USER_KEY, me.id)

      // Hydrate the store from the local cache (instant, offline).
      const [convs, msgs] = await Promise.all([readAllConversations(), readAllMessages()])
      trace('cache', `hydrate ← IndexedDB: ${convs.length} conversations, ${msgs.length} messages ${convs.length === 0 && msgs.length === 0 ? '(EMPTY — will load from server)' : '(instant paint)'}`)
      if (!disposed) {
        const store = useMessengerStore.getState()
        // Only seed the list from cache if nothing's loaded yet — don't clobber
        // a fresher REST list that may have already landed. Messages upsert
        // safely regardless (merge, never replace).
        if (convs.length > 0 && store.wireConversationOrder.length === 0) {
          store.setWireConversationList(convs, { nextCursor: null, hasMore: false }, 'replace')
        }
        const byConv = new Map<string, WireMessage[]>()
        for (const m of msgs) {
          const a = byConv.get(m.conversationId) ?? []
          a.push(m)
          byConv.set(m.conversationId, a)
        }
        for (const [id, arr] of byConv) store.upsertWireMessages(id, arr)
      }

      if (!disposed) unsub = startPersistence()
    })()

    return () => {
      disposed = true
      if (unsub) unsub()
    }
  }, [me?.id])

  // 3 — delta sync once the conversation list is available. The app-wide
  // subscription keeps things live after this; delta only catches up on what was
  // missed while away.
  //
  // RETRIABLE BY DESIGN: `syncConversation` is a SOCKET push, and on a cold start
  // (incognito / cleared storage) this races the socket build — token mint →
  // exchange → connect. A one-shot attempt that set its guard up-front and
  // swallowed per-conversation failures left the cache empty with no second try,
  // so previews and unread counts stayed blank until a manual reload. Now the
  // guard is only latched once every conversation actually synced, and a failed
  // round re-arms on a capped backoff.
  const orderLen = useMessengerStore((s) => s.wireConversationOrder.length)
  const [syncAttempt, setSyncAttempt] = useState(0)
  useEffect(() => {
    if (!me?.id || syncedRef.current || orderLen === 0) return
    let cancelled = false
    let retryTimer: ReturnType<typeof setTimeout> | null = null

    ;(async () => {
      const since = await getMeta(WATERMARK_KEY)
      const store = useMessengerStore.getState()
      const order = store.wireConversationOrder.slice()
      let maxSynced = since
      let failed = 0
      trace('cache', `delta sync starting (attempt ${syncAttempt + 1}) — since=${since ?? 'never (full)'} across ${order.length} conversation(s)`)

      await Promise.all(
        order.map(async (convId) => {
          try {
            const res = await syncConversation(me.id, convId, since, me.name)
            if (cancelled) return
            if (res.items.length > 0) {
              trace('cache', `delta: conv=${convId.slice(0, 8)} ${res.items.length} changed since watermark → store + IndexedDB`)
              store.upsertWireMessages(convId, res.items)
              void cacheMessages(res.items)
            }
            if (res.cursors.length > 0) store.seedCursors(convId, res.cursors)
            if (res.syncedAt && (!maxSynced || res.syncedAt > maxSynced)) maxSynced = res.syncedAt
          } catch {
            // Offline, socket still connecting, or channel not joined yet. Count
            // it so the round can be retried — the cache alone can't fill a cold
            // start.
            failed++
          }
        }),
      )
      if (cancelled) return

      // Advance the watermark ONLY on a fully clean round. Persisting it after a
      // partial failure would permanently skip the messages the failed
      // conversations never returned.
      if (failed === 0) {
        syncedRef.current = true
        if (maxSynced) {
          trace('cache', `watermark → IndexedDB: ${maxSynced} (next sync fetches only changes after this)`)
          await setMeta(WATERMARK_KEY, maxSynced)
        }
        return
      }

      if (syncAttempt + 1 >= SYNC_MAX_ATTEMPTS) {
        trace('cache', `delta sync gave up after ${SYNC_MAX_ATTEMPTS} attempts (${failed} conversation(s) failing) — live subscription still applies`)
        return
      }
      const delay = SYNC_RETRY_BASE_MS * 2 ** syncAttempt
      trace('cache', `delta sync: ${failed}/${order.length} conversation(s) failed → retry in ${delay}ms`)
      retryTimer = setTimeout(() => setSyncAttempt((n) => n + 1), delay)
    })()

    return () => {
      cancelled = true
      if (retryTimer) clearTimeout(retryTimer)
    }
  }, [me?.id, orderLen, syncAttempt])
}
